import _ from 'lodash'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'

/** TRANSLATION */
import { useTranslation } from 'react-i18next'

/** API */
import BanksApi from 'api/setting/finances/bank.api'


/** UTILS || SLICE || CONSTANTS */
import { resetModal, submitModal, unSubmitModal } from 'app/slice/modal.slice'
import { imgs, onImgError } from 'constants/images'
import { isCreate, isDelete, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import { fileUrl } from 'api/api'
import HeaderCard from 'component/CardCustom/HeaderCard'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableCustom from 'component/Table/TableCustom'
import TableRowCommon from 'component/Table/TableRowCommon'
import UploadImage from 'component/Upload/UploadImage'
import CheckboxButton from 'new-components/buttons/checkbox-button'
import MainButton from 'new-components/buttons/main-button'
import SwitchButton from 'new-components/buttons/switch-button'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import PageModal from 'new-components/modal/page-modal'
import SnackbarComponent from 'new-components/snackbar/snackbar-component'


const permissions = {
  isCreate: isCreate(),
  isDelete: isDelete(),
  isUpdate: isUpdate(),
  isView: isView()
}
const initStateErrorMessage = {
  NOT_FOUND_BANK: ``,
  BANK_ALREADY_SYSTEM: ``,
  INVALID_CREATED: ``,
  INVALID_UPDATED: ``,
  IS_DUPLICATE_BANK_SHORTNAME: ``,
  IS_DUPLICATE_BANK_NAME: ``,
  BANK_SHORTNAME: ``,
  BANK_SHORTNAME_STRING_BASE: ``,
  BANK_SHORTNAME_STRING_EMPTY: ``,
  BANK_SHORTNAME_MAXIMUM_LENGTH: ``,
  BANK_SHORTNAME_ANY_REQUIRED: ``,
  BANK_NAME: ``,
  BANK_NAME_STRING_BASE: ``,
  BANK_NAME_STRING_EMPTY: ``,
  BANK_NAME_MAXIMUM_LENGTH: ``,
  BANK_NAME_ANY_REQUIRED: ``,

  BANK_NAME_EN: ``,
  IS_DUPLICATE_BANK_NAME_EN: ``,
  BANK_NAME_EN_STRING_BASE: ``,
  BANK_NAME_EN_STRING_EMPTY: ``,
  BANK_NAME_EN_MAXIMUM_LENGTH: ``,
  BANK_NAME_EN_ANY_REQUIRED: ``
}
export default function Banks() {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [banks, setBanks] = useState([])

  const [bankId, setBankId] = useState(Number)
  const [bankShortname, setBankShortname] = useState('')
  const [bankName, setBankName] = useState('')
  const [hasTransfer, setHasTransfer] = useState('NOT_SUPPORT')
  const [hasCredit, setHasCredit] = useState('NOT_SUPPORT')
  const [hasDebit, setHasDebit] = useState('NOT_SUPPORT')
  const [bankStatus, setBankStatus] = useState('NOT_SUPPORT')
  const [image, setImage] = useState({
    fileId: null,
    base64: '',
    fileExtension: '',
    fileSize: ''
  })
  const [imgData, setImgData] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState(initStateErrorMessage)

  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('createAt')
  const [sortType, setSortType] = useState('ASC')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('ALL')
  const [disabledSubmit, setDisabledSubmit] = useState(false)
  const [isOpenAlertModal, setIsOpenAlertModal] = useState(false)
  const [statusAlertModal, setStatusAlertModal] = useState('')
  const [id, setId] = useState<number>(0)
  const [name, setName] = useState('')
  const [isOpenSnack, setIsOpenSnack] = useState(false)
  const [typeSnack, setTypeSnack] = useState<"success" | "error" | "warning" | "info" | any>("success")
  const [labelSnack, setLabelSnack] = useState('')

  const [isPopup, setIsPopup] = useState(false)


  const loadData = async () => {
    try {
      const resp = await BanksApi.findAll({
        page: page,
        pageLimit: pageLimit,
        search: search,
        sortBy: sortBy,
        sortType: sortType,
        status: status === 'ALL' ? '' : status
      })
      if (resp.status === 200) {
        setRowCount(resp.headers['x-total'])
        setBanks(resp.data)
      } else {
        setBanks([])
      }
    } catch (error) {
      notiError('ERROR')
    }
  }

  useEffect(() => {
    loadData()
  }, [page, pageLimit, sortBy, sortType])


  const submit = () => {
    if (!bankShortname || !bankName) {
      return setErrorMessage({ ...errorMessage, ...{ BANK_SHORTNAME: !bankShortname ? t(`BANK.MESSAGE.BANK_SHORTNAME`) : '', BANK_NAME: !bankName ? t(`BANK.MESSAGE.BANK_NAME`) : '' } })
    }

    dispatch(submitModal())
    setDisabledSubmit(true)
    BanksApi.create({ bankShortname, bankName, hasTransfer, hasCredit, hasDebit, image })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 201) {
          notiSuccess(t(`BANK.MESSAGE.SUCCESS.CREATE`), '', null, null)
          resetForm()

          loadData()
          dispatch(resetModal())
        } else {
          const err = (res as { response: { data: { message: string } } }).response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`BANK.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e: { response: { data: { message: string } } }) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`BANK.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      }).finally(() => setDisabledSubmit(false))
  }

  const submitEdit = () => {

    if (!bankShortname || !bankName) {
      return setErrorMessage({ ...errorMessage, ...{ BANK_SHORTNAME: !bankShortname ? t(`BANK.MESSAGE.BANK_SHORTNAME`) : '', BANK_NAME: !bankName ? t(`BANK.MESSAGE.BANK_NAME`) : '' } })
    }

    dispatch(submitModal())
    setDisabledSubmit(true)
    BanksApi.update(bankId, { bankShortname, bankName, hasTransfer, hasCredit, hasDebit, status: bankStatus, image })
      .then((res: any) => {
        if (res.status !== undefined && res.status === 200) {
          notiSuccess(t(`BANK.MESSAGE.SUCCESS.UPDATE`), '', null, null)
          resetForm()
          loadData()
          dispatch(resetModal())
        } else {
          const err = (res as { response: { data: { message: string } } }).response.data
          setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`BANK.MESSAGE.${err.message}`) } })
          dispatch(unSubmitModal())
        }
      })
      .catch((e: { response: { data: { message: string } } }) => {
        const err = e.response.data
        setErrorMessage({ ...errorMessage, ...{ [err.message]: t(`BANK.MESSAGE.${err.message}`) } })
        dispatch(unSubmitModal())
      }).finally(() => setDisabledSubmit(false))
  }

  const clearErrorMessage = () => {
    setErrorMessage(initStateErrorMessage)
  }

  const resetForm = () => {
    setBankId(0)
    setBankShortname('')
    setBankName('')
    setHasDebit('NOT_SUPPORT')
    setHasCredit('NOT_SUPPORT')
    setHasTransfer('NOT_SUPPORT')
    setImgData('')
    setImage({
      fileId: null,
      base64: '',
      fileExtension: '',
      fileSize: ''
    })
    setIsPopup(false)
    clearErrorMessage()
  }
  const onActive = () => {
    if (id) {
      BanksApi.updateStatus(id, statusAlertModal).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('BANK.MESSAGE.SUCCESS.UPDATE_STATUS'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('BANK.MESSAGE.ERROR'))
        }
      })
    }
  }
  const onDelete = () => {
    if (id) {
      BanksApi.remove(id).then((resp) => {
        if (resp.status === 200) {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("success")
          setLabelSnack(t('BANK.MESSAGE.SUCCESS.DELETE'))
          loadData()
        } else {
          setIsOpenAlertModal(false)
          setIsOpenSnack(true)
          setTypeSnack("error")
          setLabelSnack(t('BANK.MESSAGE.ERROR'))
        }
      })
    }
  }

  const onEdit = (data: any) => {
    const { bankId: bkId, bankShortname: bkShortName, bankName: bkName, hasDebit: bkHasDebit, hasCredit: bkHasCredit, hasTransfer: bkHasTransfer, status: bkStatus, image: bkImage } = data
    setBankId(bkId)
    setBankShortname(bkShortName)
    setBankName(bkName)
    setHasDebit(bkHasDebit)
    setHasCredit(bkHasCredit)
    setHasTransfer(bkHasTransfer)
    setBankStatus(bkStatus)
    if (bkImage) {
      setImgData(`${fileUrl}/${bkImage.filePath}/${bkImage.fileName}`)
      setImage({ fileId: data.image.fileId, base64: '', fileExtension: '', fileSize: '' })
    }
    setIsPopup(true)
  }

  const onRequestSort = (sortByVal: string, sortTypeVal: string) => {
    setSortType(sortTypeVal)
    setSortBy(sortByVal)
  }

  const handleChangePage = (val: number) => {
    setPage(val)
  }

  const handleChangeRowsPerPage = (limit: number) => {
    setPageLimit(limit)
  }

  const handleChangeStatus = (statusVal: string) => {
    setStatus(statusVal)
  }

  const uploadImage = (files: any) => {
    if (files[0]) {
      const file = files[0]
      const fileName = file.name.split('.')
      const detailImage = {
        fileId: null,
        base64: '',
        fileExtension: fileName[fileName.length - 1],
        fileSize: file.size.toString()
      }
      const reader: any = new FileReader()
      reader.addEventListener('load', () => {
        const base64 = reader.result.split(',')
        setImgData(reader.result)
        detailImage.base64 = base64.length > 0 ? base64[base64.length - 1] : reader.result
      })
      reader.readAsDataURL(file)
      setImage(detailImage)
    }
  }

  const headCells = [
    { id: 'no', disablePadding: false, label: '#' },
    { id: 'bankLogo', disablePadding: false, align: 'center', label: t('BANK.TABLE.CELL.BANK_LOGO') },
    { id: 'bankShortname', disablePadding: false, label: t('BANK.TABLE.CELL.BANK_SHORTNAME') },
    { id: 'bankName', disablePadding: false, label: t('BANK.TABLE.CELL.BANK_NAME') },
    { id: 'hasTransfer', disablePadding: false, align: 'center', label: t('BANK.TABLE.CELL.HAS_TRANSFER') },
    { id: 'hasDebit', disablePadding: false, align: 'center', label: t('BANK.TABLE.CELL.HAS_CREDIT') },
    { id: 'hasCredit', disablePadding: false, align: 'center', label: t('BANK.TABLE.CELL.HAS_DEBIT') },
    { id: 'status', disablePadding: false, label: t('BANK.TABLE.CELL.STATUS') },
    { id: 'updatedBy', disablePadding: false, label: t('BANK.TABLE.CELL.UPDATED_BY') },
    { id: 'action', disablePadding: false, label: 'Action' }
  ]

  const renderData = (objData: any, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { bankId: bkId, bankShortname: bkShortName, bankName: bkName, hasTransfer: bkHasTransfer, hasDebit: bkHasDebit, hasCredit: bkHasCredit, updatedBy, updatedAt, status: bkStatus, image: bkImage, bankLogo: bkLogo } = objData
    const statusBtnActive = bkStatus === 'ACTIVE' ? true : permissions.isUpdate.disabled
    const statusBtnInActive = bkStatus === 'INACTIVE' ? true : permissions.isUpdate.disabled
    const renderImage = (img: any, valueBkLogo: string) => {
      if (!_.isEmpty(img) && !_.isEmpty(img.filePath) && !_.isEmpty(img.fileName)) return `${fileUrl}/${img.filePath}/${img.fileName}`
      else if (valueBkLogo) return `${fileUrl}/${valueBkLogo}`
      else return imgs.errNoImg
    }
    const objRenderData = {
      key: bkId,
      id: bkId,
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        {
          option: 'COMPONENT', align: 'center', component: (
            <Box display="flex" justifyContent="center" alignItems="center">
              <Box
                component="img"
                sx={{ width: 35, height: 35, objectFit: 'cover' }}
                src={renderImage(bkImage, bkLogo)}
                onError={onImgError}
                alt=""
              />
            </Box>
          )
        },
        { option: 'TEXT', align: 'left', label: bkShortName },
        { option: 'TEXT', align: 'left', label: bkName },
        {
          option: 'COMPONENT', align: 'center', component: (
            <Box>
              <CheckboxButton
                disabled
                defaultChecked={bkHasTransfer === "SUPPORT"}
                checked={bkHasTransfer === 'SUPPORT' ? true : false}
                onChange={() => null} />
            </Box>
          )
        },
        {
          option: 'COMPONENT', align: 'center', component: (
            <Box>
              <CheckboxButton
                disabled
                defaultChecked={bkHasCredit === "SUPPORT"}
                checked={bkHasCredit === 'SUPPORT' ? true : false}
                onChange={() => null} />
            </Box>
          )
        },
        {
          option: 'COMPONENT', align: 'center', component: (
            <Box>
              <CheckboxButton
                disabled
                defaultChecked={bkHasDebit === "SUPPORT"}
                checked={bkHasDebit === 'SUPPORT' ? true : false}
                onChange={() => null} />
            </Box>
          )
        },
        { option: 'STATUS', align: 'center', label: bkStatus },
        { option: 'UPDATE_BY', align: 'center', label: { updatedBy: updatedBy, updatedAt: updatedAt } },
        {
          option: 'ACTION',
          align: 'center',
          label: 'action',
          values: [
            { option: 'STATUS_ACTIVE', label: t(`STATUS.ACTIVE`), disabled: statusBtnActive },
            { option: 'STATUS_INACTIVE', label: t(`STATUS.INACTIVE`), disabled: statusBtnInActive },
            { option: 'DIVIDER', label: '', disabled: false },
            { option: 'EDIT', label: t(`BUTTON.EDIT`), disabled: permissions.isUpdate.disabled },
            { option: 'DELETE', label: t(`BUTTON.DELETE`), disabled: permissions.isDelete.disabled }
          ]
        }
      ]
    }
    return (
      <TableRowCommon
        {...objRenderData}
        onactive={() => handleSubmitModal(Number(objRenderData.id), 'ACTIVE', objRenderData.obj)}
        oninactive={() => handleSubmitModal(Number(objRenderData.id), 'INACTIVE', objRenderData.obj)}
        onedit={() => onEdit(objRenderData.obj)}
        ondelete={() => handleSubmitModal(Number(objRenderData.id), 'DELETE', objRenderData.obj)}
      />
    )
  }

  const handleSubmitModal = (id: number, status: string, obj?: any) => {
    setStatusAlertModal(status)
    setId(id)
    setIsOpenAlertModal(true)
    setName(obj.bankName)
  }

  return (
    <Box>
      <AlertModal
        isOpen={isOpenAlertModal}
        title={t(`BANK.ALERT.${statusAlertModal === 'DELETE' ? 'TITLE_DELETE' : 'TITLE_ACTIVE'}`)}
        message={t(`BANK.ALERT.${statusAlertModal === 'DELETE' ? 'CONFIRM_DELETE_' : 'CONFIRM_STATUS'}`, { bankName: name, status: t(`BANK.ALERT.${statusAlertModal}`) })}
        type={statusAlertModal === 'DELETE' ? 'error' : 'warning'}
        handleClose={() => setIsOpenAlertModal(false)}
        onCancel={{ onClick: () => setIsOpenAlertModal(false), title: t('BUTTON.CANCEL') }}
        onSubmit={{ onClick: statusAlertModal === 'DELETE' ? onDelete : onActive, title: t('BUTTON.SUBMIT') }}
      />
      <SnackbarComponent
        onOpen={isOpenSnack}
        onClose={() => setIsOpenSnack(false)}
        label={labelSnack}
        type={typeSnack}
        transform={`translateX(calc(100vw - 100% - 32px)) translateY(calc(-100vh + 100% + ${window.innerHeight - 50}px))`}
        autoHideTime={3000}
      />
      <HeaderCard text={t('BANK.TITLE')} />
      <Box paddingX="24px" paddingTop="16px">
        <Grid container spacing="16px">
          <Grid item xs={2}>
            <TextInput
              label={t('BANK.SEARCH')}
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              fullWidth
              inputProps={{
                endAdornment: (
                  <InputAdornment position="end" >
                    {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                      <FontAwesomeIcon
                        icon={faTimes}
                        onClick={() => {
                          setSearch('');
                        }}
                        style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={2}>
            <SelectInput
              onChange={(event) => {
                const value = event.target.value
                if (value) {
                  handleChangeStatus(value)
                }
              }}
              options={[
                { value: 'ALL', name: t('STATUS.ALL') },
                { value: 'ACTIVE', name: t('STATUS.ACTIVE') },
                { value: 'INACTIVE', name: t('STATUS.INACTIVE') }
              ]}
              label={t('STATUS.LABEL')}
              value={status}
              width="100%"
            />
          </Grid>
          <Grid item xs={1}>
            <MainButton
              title={t('INPUT.SEARCH')}
              type='primary'
              variant='contained'
              style={{ width: '120px' }}
              onClick={() => {
                setPage(1)
                loadData()
              }} />
          </Grid>
          <Grid item xs={7} display="flex" justifyContent="flex-end">
            <MainButton
              title={t('BANK.BUTTON.ADD')}
              disabled={permissions.isCreate.disabled}
              type='primary'
              variant='contained'
              onClick={() => setIsPopup(true)}
              startIcon={<AddCircleIcon />}
            />
          </Grid>
        </Grid>
        <Box paddingTop="24px">
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            onSort={onRequestSort}
            setPageLimit={handleChangeRowsPerPage}
            setPage={handleChangePage}
            rowCount={rowCount}
            headCells={headCells}
            rowsData={banks.map((val, i) => {
              return renderData(val, i)
            })}
          />
        </Box>
        <PageModal
          title={bankId ? t('BANK.TITLE_UPDATE') : t('BANK.TITLE_CREATE')}
          isOpen={isPopup}
          onCancel={{ onClick: () => resetForm() }}
          onSubmit={{ onClick: () => bankId ? submitEdit() : submit(), disabled: disabledSubmit }}
          handleClose={resetForm}
          size={'XS'}
          overflow
        >
          <Box py={2}>
            {bankId ? (
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography>{t('INPUT.LABEL_STATUS')}</Typography>
                <SwitchButton
                  onChange={() => setBankStatus(bankStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE')}
                  checked={bankStatus === 'ACTIVE' ? true : false}
                />
              </Box>
            ) : (
              ''
            )}
            <Box display={'flex'} flexDirection={'column'} gap={'24px'}>
              <Box margin={'8px'}>
                <UploadImage setValue={uploadImage} pathFile={imgData} />
              </Box>
              <Box>
                <TextInput
                  fullWidth
                  onChange={(event) => {
                    setBankShortname(event.target.value)
                    clearErrorMessage()
                  }}
                  inputProps={{ maxLength: 100 }}
                  value={bankShortname}
                  label={t('BANK.INPUT.BANK_SHORTNAME')}
                  helperText={
                    errorMessage.BANK_SHORTNAME ||
                    errorMessage.IS_DUPLICATE_BANK_SHORTNAME ||
                    errorMessage.BANK_SHORTNAME_STRING_BASE ||
                    errorMessage.BANK_SHORTNAME_STRING_EMPTY ||
                    errorMessage.BANK_SHORTNAME_MAXIMUM_LENGTH ||
                    errorMessage.BANK_SHORTNAME_ANY_REQUIRED
                  }
                  required={true}
                />
              </Box>
              <Box>
                <TextInput
                  fullWidth
                  onChange={(event) => {
                    setBankName(event.target.value)
                    clearErrorMessage()
                  }}
                  inputProps={{ maxLength: 150 }}
                  value={bankName}
                  label={t('BANK.INPUT.BANK_NAME')}
                  helperText={errorMessage.BANK_NAME ||
                    errorMessage.IS_DUPLICATE_BANK_NAME ||
                    errorMessage.BANK_NAME_STRING_EMPTY ||
                    errorMessage.BANK_NAME_STRING_EMPTY ||
                    errorMessage.BANK_NAME_MAXIMUM_LENGTH ||
                    errorMessage.BANK_NAME_ANY_REQUIRED}
                  required={true}
                />
              </Box>
              <Box display={'flex'} flexDirection={'column'} gap={'16px'}>
                <Box >{t('BANK.LABEL.SERVICE')}</Box>
                <Box display="flex" flexWrap="wrap" gap="48px">
                  <CheckboxButton
                    label={t('BANK.LABEL.TRANSFER')}
                    checked={hasTransfer === 'SUPPORT' ? true : false}
                    onChange={(event) => {
                      setHasTransfer(event.target.checked ? 'SUPPORT' : 'NOT_SUPPORT')
                    }}
                  />
                  <CheckboxButton
                    label={t('BANK.LABEL.CREDIT')}
                    checked={hasCredit === 'SUPPORT' ? true : false}
                    onChange={(event) => {
                      setHasCredit(event.target.checked ? 'SUPPORT' : 'NOT_SUPPORT')
                    }}
                  />
                  <CheckboxButton
                    label={t('BANK.LABEL.DEBIT')}
                    checked={hasDebit === 'SUPPORT' ? true : false}
                    onChange={(event) => {
                      setHasDebit(event.target.checked ? 'SUPPORT' : 'NOT_SUPPORT')
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </PageModal>
      </Box>
    </Box>
  )
}
